import { cn } from "~/lib/utils";
import { toneFor, type Tone } from "~/components/status-badge";

/**
 * The row of one-click status presets both debugging screens open on.
 *
 * The webhook events and callback deliveries screens deliberately land on their
 * failures -- `?status=failed` and `?status=exhausted` -- rather than on a
 * chronological firehose. The failing rows are why anyone opens either screen,
 * and a page that opens on a thousand successful deliveries makes you filter
 * before you can start.
 *
 * The preset writes to the URL like every other filter, so what is on screen and
 * what the address bar says can never disagree, and a paste of that link during
 * an incident shows the same table. That is the whole reason this is a chip row
 * over the search params rather than a default sort hidden in the query.
 */
export function FilterChips({
  value,
  options,
  onChange,
  className,
}: {
  /** The active status, or undefined for "All". */
  value: string | undefined;
  options: readonly string[];
  /** undefined clears the filter. */
  onChange: (next: string | undefined) => void;
  className?: string;
}) {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-1.5", className)}
      role="group"
      aria-label="Status presets"
    >
      <Chip active={value === undefined} onClick={() => onChange(undefined)}>
        All
      </Chip>
      {options.map((option) => (
        <Chip
          key={option}
          active={value === option}
          tone={toneFor(option)}
          onClick={() => onChange(option)}
        >
          {option}
        </Chip>
      ))}
    </div>
  );
}

/**
 * One preset.
 *
 * The active chip carries its status tone; the inactive ones stay neutral. A row
 * where every chip is already coloured is a row where the selected one is hard
 * to find, and the tone is doing real work here -- it is the same red that marks
 * a failed row in the table below.
 */
function Chip({
  active,
  tone,
  onClick,
  children,
}: {
  active: boolean;
  tone?: Tone;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-md border px-2.5 py-1 text-xs font-medium capitalize transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none",
        active
          ? ACTIVE[tone ?? "neutral"]
          : "border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {children}
    </button>
  );
}

/** The active chip's colours, on the same five tones as StatusBadge. */
const ACTIVE: Record<Tone, string> = {
  success: "border-success/40 bg-success/15 text-success",
  warning: "border-warning/45 bg-warning/20 text-warning",
  danger: "border-destructive/40 bg-destructive/15 text-destructive",
  info: "border-info/40 bg-info/15 text-info",
  neutral: "border-foreground/25 bg-foreground/10 text-foreground",
};
